import type { CompanionRecommendationSport } from "@/lib/companion/recommendation-types";

export const PUBLIC_EVENT_CATEGORIES = [
  "resort-town",
  "community-meetup",
  "competition",
  "music-nightlife",
  "lesson-intro",
  "festival",
  "access-transport",
] as const;

export const PUBLIC_EVENT_TEXT_LIMITS = {
  name: 160,
  description: 4_000,
  venueLabel: 200,
  timezone: 64,
  canonicalSlug: 96,
  eventSlug: 120,
  sourceUrl: 2_048,
  internalPath: 512,
  originKey: 200,
  submitterResponse: 500,
  privateReasonOrEvidence: 1_000,
} as const;

export type PublicEventCategory = (typeof PUBLIC_EVENT_CATEGORIES)[number];

export type PublicEventSportKey = CompanionRecommendationSport | "festival";

export type PublicEventSourceKind =
  | "community"
  | "editorial"
  | "peakwave-festival"
  | "trusted-source";

export type PublicEventPublicationStatus =
  | "published"
  | "cancelled"
  | "hidden";

export type PublicEventSubmissionState =
  | "pending"
  | "published"
  | "rejected"
  | "withdrawn"
  | "expired";

export type PublicEventSummary = {
  id: string;
  slug: string;
  name: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  venueLabel: string;
  conditionLocationSlug: string | null;
  regionSlug: string | null;
  latitude: number | null;
  longitude: number | null;
  category: PublicEventCategory;
  sportKeys: PublicEventSportKey[];
  sourceKind: PublicEventSourceKind;
  organiserName?: string | null;
  publicationStatus: PublicEventPublicationStatus;
  externalSourceUrl: string | null;
  internalSourcePath: string | null;
  festivalSlug: string | null;
  isVerified: boolean;
  verifiedAt: string | null;
  originKey: string;
  updatedAt: string;
};

export type PublicEventDetail = PublicEventSummary & {
  description: string | null;
};

export type PublicEventSubmissionInput = {
  name: string;
  description: string | null;
  startsLocal: string;
  endsLocal: string;
  timezone: string;
  venueLabel: string;
  conditionLocationSlug: string | null;
  regionSlug: string | null;
  latitude: number | null;
  longitude: number | null;
  category: PublicEventCategory;
  sportKeys: PublicEventSportKey[];
  externalSourceUrl: string | null;
};

export type PublicEventCatalogueInput = PublicEventSubmissionInput & {
  slug: string;
  internalSourcePath: string | null;
  festivalSlug: string | null;
};

export type PublicEventActionResult =
  | {
      ok: true;
      outcome: "pending" | "withdrawn" | "unchanged";
      submissionId: string;
      eventCategory: PublicEventCategory;
    }
  | {
      ok: false;
      code:
        | "signed-out"
        | "invalid"
        | "rate-limited"
        | "conflict"
        | "forbidden"
        | "not-found"
        | "unavailable";
      error: string;
      fieldErrors?: Partial<Record<keyof PublicEventSubmissionInput, string>>;
    };

export type PublicEventAdminActionResult =
  | {
      ok: true;
      outcome: "updated" | "unchanged";
      eventId: string | null;
    }
  | {
      ok: false;
      code: "not-admin" | "invalid" | "conflict" | "not-found" | "unavailable";
      error: string;
    };

export type PublicEventLocationOption = {
  slug: string;
  name: string;
  regionSlug: string | null;
  timezone: string | null;
};

export type PublicEventOrganiser = {
  id: string;
  name: string;
  slug: string;
  websiteUrl: string | null;
  companyId: string | null;
  venueLabel: string | null;
  timezone: string | null;
  conditionLocationSlug: string | null;
  regionSlug: string | null;
  latitude: number | null;
  longitude: number | null;
  status: "active" | "suspended";
};

export type PublicEventOrganiserInput = {
  name: string;
  websiteUrl: string | null;
  venueLabel: string | null;
  timezone: string | null;
  conditionLocationSlug: string | null;
  regionSlug: string | null;
  latitude: number | null;
  longitude: number | null;
};

export type PublicEventPublishInput = PublicEventSubmissionInput & {
  organiserId: string;
  repeatWeeks: number;
};

export type PublicEventManagedItem = PublicEventDetail & {
  organiserId: string;
  seriesId: string | null;
  recurrenceIndex: number | null;
  insights: {
    detailViews: number;
    saved: number;
    going: number;
    calendarExports: number;
    sourceClicks: number;
  };
};

export type PublicEventResponseState = "saved" | "going";

export type PublicEventResponse = {
  state: PublicEventResponseState;
  reminderEnabled: boolean;
};

export type PublicEventOrganiserActionResult =
  | {
      ok: true;
      outcome:
        | "abandoned"
        | "expired"
        | "organiser-created"
        | "organiser-existing"
        | "published"
        | "updated"
        | "cancelled"
        | "restored";
      organiserId?: string;
      eventIds?: string[];
      eventCategory?: PublicEventCategory;
    }
  | {
      ok: false;
      code:
        | "signed-out"
        | "invalid"
        | "rate-limited"
        | "conflict"
        | "forbidden"
        | "not-found"
        | "unavailable";
      error: string;
      fieldErrors?: Record<string, string>;
    };

export type PublicEventResponseActionResult =
  | {
      ok: true;
      response: PublicEventResponse | null;
    }
  | {
      ok: false;
      code: "signed-out" | "invalid" | "not-found" | "unavailable";
      error: string;
    };
